import styled, { css } from "styled-components";
import { SunsetPhase } from "./utils";

type Variant = "warm" | "dark" | "muted";

export const phaseVariant: Record<SunsetPhase, Variant> = {
  announced: "warm",
  finalWeeks: "dark",
  closed: "muted",
  exportsEnded: "muted",
};

const palette = {
  warm: {
    bg: "#F9F5EE",
    border: "#F9F5EE",
    title: "#000000",
    text: "#525252",
    body: "#475569",
    iconBg: "#FDE3D6",
    iconColor: "var(--primary-brand)",
    count: "var(--primary-brand)",
    track: "#E8DFD0",
    fill: "var(--primary-brand)",
    label: "#000000",
    labelMuted: "#757575",
  },
  dark: {
    bg: "#0F172A",
    border: "#0F172A",
    title: "#FFFFFF",
    text: "#CBD5E1",
    body: "#CBD5E1",
    iconBg: "#2A2F3D",
    iconColor: "var(--primary-brand)",
    count: "var(--primary-brand)",
    track: "#334155",
    fill: "var(--primary-brand)",
    label: "#FFFFFF",
    labelMuted: "#CBD5E1",
  },
  muted: {
    bg: "#F1F5F9",
    border: "#E2E8F0",
    title: "#000000",
    text: "#525252",
    body: "#475569",
    iconBg: "#E2E8F0",
    iconColor: "#475569",
    count: "#334155",
    track: "#CBD5E1",
    fill: "#334155",
    label: "#000000",
    labelMuted: "#757575",
  },
};

export const Wrapper = styled.section<{ $variant: Variant }>`
  font-family: "Inter";
  background: ${({ $variant }) => palette[$variant].bg};
  border: 1px solid ${({ $variant }) => palette[$variant].border};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  color: ${({ $variant }) => palette[$variant].text};
`;

export const Top = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
  }
`;

export const Main = styled.div`
  display: flex;
  gap: 16px;
`;

export const IconBox = styled.div<{ $variant: Variant }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $variant }) => palette[$variant].iconBg};
  color: ${({ $variant }) => palette[$variant].iconColor};
  font-size: 18px;
`;

export const Content = styled.div<{ $variant: Variant }>`
  h2 {
    font-family: "Suisse International", sans-serif;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: -0.4px;
    color: ${({ $variant }) => palette[$variant].title};
    margin: 0 0 6px;
  }

  p {
    font-family: "Inter";
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    letter-spacing: 0;
    color: ${({ $variant }) => palette[$variant].body};
    margin: 0;
    max-width: 680px;
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

export const ActionButton = styled.button<{ $kind: "brand" | "black" | "outline"; $variant: Variant }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  cursor: pointer;
  border: none;

  ${({ $kind, $variant }) => {
    if ($kind === "brand") {
      return css`
        background: #ff4a01;
        color: #ffffff;
        &:hover { background: var(--primary-brand-hover); }
      `;
    }
    if ($kind === "black") {
      return css`
        background: var(--primary-black);
        color: #ffffff;
        &:hover { background: #000000; }
      `;
    }
    return $variant === "dark"
      ? css`
          background: transparent;
          color: #ffffff;
          border: 1px solid #475569;
          &:hover { border-color: #ffffff; }
        `
      : css`
          background: #ffffff;
          color: #000000;
          border: 1px solid #e2e8f0;
          &:hover { border-color: #000000; }
        `;
  }}
`;

export const Counter = styled.div<{ $variant: Variant }>`
  flex-shrink: 0;
  text-align: left;

  strong {
    display: block;
    font-family: "Suisse International", sans-serif;
    font-size: 36px;
    font-weight: 500;
    line-height: 40px;
    color: ${({ $variant }) => palette[$variant].count};
  }

  span {
    display: block;
    font-family: "Inter";
    font-size: 12px;
    font-weight: 400;
    line-height: 100%;
    letter-spacing: 0;
    color: ${({ $variant }) => palette[$variant].body};
    max-width: 120px;
  }

  @media (min-width: 768px) {
    text-align: right;

    span {
      margin-left: auto;
    }
  }
`;

export const Timeline = styled.div<{ $variant: Variant }>`
  position: relative;
  margin-top: 24px;

  ol {
    display: flex;
    justify-content: space-between;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    line-height: 16px;
    color: ${({ $variant }) => palette[$variant].labelMuted};
    z-index: 1;
  }

  li:first-child { align-items: flex-start; }
  li:last-child { align-items: flex-end; }

  li b {
    font-weight: 500;
    color: ${({ $variant }) => palette[$variant].label};
  }

  @media (max-width: 767px) {
    li span { display: none; }
  }
`;

export const Track = styled.div<{ $variant: Variant; $progress: number }>`
  position: absolute;
  top: 5px;
  left: 6px;
  right: 6px;
  height: 2px;
  background: ${({ $variant }) => palette[$variant].track};

  &::after {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: ${({ $progress }) => $progress}%;
    background: ${({ $variant }) => palette[$variant].fill};
  }
`;

export const Dot = styled.i<{ $variant: Variant; $reached: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid
    ${({ $variant, $reached }) => ($reached ? palette[$variant].fill : palette[$variant].track)};
  background: ${({ $variant, $reached }) =>
    $reached ? palette[$variant].fill : palette[$variant].bg};
`;
